export interface MediaDimensions {
  width: number;
  height: number;
}

/**
 * Read an image file's intrinsic pixel size. Uses `createImageBitmap`
 * (decodes off the main thread, no DOM node) and always closes the
 * bitmap so the decoded pixels are released.
 */
export async function readImageDimensions(
  file: File,
): Promise<MediaDimensions> {
  const bitmap = await createImageBitmap(file);
  try {
    return { width: bitmap.width, height: bitmap.height };
  } finally {
    bitmap.close();
  }
}

/**
 * Read a video file's intrinsic pixel size by probing metadata off a
 * detached `<video>` element. Resolves once `loadedmetadata` fires;
 * rejects on decode error. The object URL is always revoked.
 */
export async function readVideoDimensions(
  file: File,
): Promise<MediaDimensions> {
  const objectUrl = URL.createObjectURL(file);
  const video = document.createElement('video');
  try {
    return await new Promise<MediaDimensions>((resolve, reject) => {
      video.preload = 'metadata';
      video.muted = true;
      video.onloadedmetadata = () => {
        resolve({ width: video.videoWidth, height: video.videoHeight });
      };
      video.onerror = () => {
        reject(video.error ?? new Error('Failed to read video metadata'));
      };
      video.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
